import { useId, useMemo, useState, type KeyboardEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

export type FamilyOption = {
  familyId: string;
  familyName?: string | null;
  childNames?: string[];
  /** Children a part of a payment can be meant for. */
  children?: Array<{ childId: string; name: string }>;
};

const MAX_RESULTS = 8;

const normalize = (value?: string | null): string =>
  String(value ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/**
 * Families whose name (or one of whose children) matches the query: names that start with it first, then names
 * that contain it, then families found through a child's name. Accents do not matter ("zoe" finds "Zoë").
 */
export function matchFamilies(options: FamilyOption[], query: string, limit = MAX_RESULTS): FamilyOption[] {
  const q = normalize(query.trim());
  if (!q) return [];
  const scored: Array<{ option: FamilyOption; score: number }> = [];
  options.forEach((option) => {
    const name = normalize(option.familyName);
    const score = name.startsWith(q) ? 0
      : name.split(/[\s-]+/).some((word) => word.startsWith(q)) ? 1
        : name.includes(q) ? 2
          : (option.childNames ?? []).some((child) => normalize(child).includes(q)) ? 3 : -1;
    if (score >= 0) scored.push({ option, score });
  });
  return scored
    .sort((a, b) => a.score - b.score || String(a.option.familyName ?? "").localeCompare(String(b.option.familyName ?? ""), "nl"))
    .slice(0, limit)
    .map((entry) => entry.option);
}

/** A line in the list: a family, or (when children can be chosen) one child of a family. */
export type PickerEntry = { key: string; familyId: string; familyName?: string | null; childId?: string; childName?: string; childNames?: string[] };

/**
 * Like matchFamilies, but a child whose name matches comes up as its own line, so it can be chosen directly.
 * Children whose first or last name starts with the text come right after families that start with it.
 */
export function matchEntries(options: FamilyOption[], query: string, limit = MAX_RESULTS): PickerEntry[] {
  const q = normalize(query.trim());
  if (!q) return [];
  const scored: Array<{ entry: PickerEntry; score: number; sort: string }> = [];
  options.forEach((option) => {
    const name = normalize(option.familyName);
    const familyScore = name.startsWith(q) ? 0 : name.split(/[\s-]+/).some((word) => word.startsWith(q)) ? 1 : name.includes(q) ? 2 : -1;
    if (familyScore >= 0) {
      scored.push({ entry: { key: option.familyId, familyId: option.familyId, familyName: option.familyName, childNames: option.childNames },
        score: familyScore, sort: name });
    }
    (option.children ?? []).forEach((child) => {
      const childName = normalize(child.name);
      const childScore = childName.split(/[\s-]+/).some((word) => word.startsWith(q)) ? 1 : childName.includes(q) ? 3 : -1;
      // A child found only through its family name is not listed separately (the family line covers it).
      if (childScore >= 0 && !(familyScore >= 0 && !childName.split(/[\s-]+/)[0].startsWith(q))) {
        scored.push({ entry: { key: `${option.familyId}:${child.childId}`, familyId: option.familyId, familyName: option.familyName,
          childId: child.childId, childName: child.name }, score: childScore + 0.5, sort: childName });
      }
    });
  });
  return scored
    .sort((x, y) => x.score - y.score || x.sort.localeCompare(y.sort, "nl"))
    .slice(0, limit)
    .map((item) => item.entry);
}

/**
 * Type to search a family; matching families appear in a list to pick from (mouse or arrow keys + Enter). With
 * {@code withChildren}, children show up as their own lines too: picking one gives the family and the child.
 */
export default function FamilyPicker({ options, value, onChange, label, disabled, withChildren = false }: {
  options: FamilyOption[];
  value: string;
  onChange: (familyId: string, childId?: string) => void;
  label: string;
  disabled?: boolean;
  withChildren?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const listId = useId();
  const selected = options.find((option) => option.familyId === value);
  const [query, setQuery] = useState(selected?.familyName ?? "");
  const [chosenText, setChosenText] = useState(selected?.familyName ?? "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const matches = useMemo<PickerEntry[]>(() => (withChildren
    ? matchEntries(options, query)
    : matchFamilies(options, query).map((option) => ({ key: option.familyId, familyId: option.familyId, familyName: option.familyName, childNames: option.childNames }))),
  [options, query, withChildren]);
  const showList = open && query.trim().length > 0 && !(value && query === chosenText);

  const choose = (entry: PickerEntry) => {
    onChange(entry.familyId, entry.childId);
    const text = entry.childId ? `${entry.childName}` : entry.familyName ?? "";
    setQuery(text);
    setChosenText(text);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && showList && matches[active]) {
      event.preventDefault();
      choose(matches[active]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative w-full max-w-[16rem]">
      <input
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && matches[active] ? `${listId}-${matches[active].key}` : undefined}
        autoComplete="off"
        disabled={disabled}
        value={query}
        placeholder={t("finance.payments.search_family", { defaultValue: "Type a family or child name…" })}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setOpen(true);
          if (value) onChange("");
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className={`h-9 w-full rounded-lg border bg-white px-2 text-sm dark:bg-slate-900 ${value
          ? "border-emerald-300 font-semibold dark:border-emerald-400/40"
          : "border-slate-200 dark:border-white/10"}`}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 z-40 mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-white/10 dark:bg-slate-900"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-slate-500">{t("finance.payments.no_family_found", { defaultValue: "No family found" })}</li>
          ) : matches.map((option, index) => (
            <li
              key={option.key}
              id={`${listId}-${option.key}`}
              role="option"
              aria-selected={index === active}
              // mousedown instead of click: the input would lose focus (and close the list) before a click lands.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setActive(index)}
              className={`cursor-pointer px-3 py-1.5 ${index === active ? "bg-indigo-50 dark:bg-white/10" : ""}`}
            >
              {option.childId ? (
                <>
                  <span className="font-semibold text-slate-900 dark:text-white">{option.childName}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {t("finance.payments.child_of_family", { name: option.familyName || "—", defaultValue: "child · family {{name}}" })}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-900 dark:text-white">{option.familyName || "—"}</span>
                  {option.childNames && option.childNames.length > 0 && (
                    <span className="block truncate text-xs text-slate-500">{option.childNames.join(", ")}</span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
